import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { ProductGroups } from 'enums';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  columns: { text: string; type: string }[];
  fileName: string;
  hasTableHeading: boolean;
  productGroup: ProductGroups;

  handleColumnTypeChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleHasTableHeadingChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleClose(): void;
  handleAcceptColumns(): void;
}

const FileUploadDialog: React.FC<Props> = ({
  open,
  columns,
  fileName,
  hasTableHeading,
  productGroup,
  handleColumnTypeChange,
  handleHasTableHeadingChange,
  handleClose,
  handleAcceptColumns,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  // 1) Added phone number here:
  const manualCampaignContactUploadOptions: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: getLocalizedText('doNotImport') },
    { value: FileUploadMatchColumns.FIRST_NAME, label: getLocalizedText('firstName') },
    { value: FileUploadMatchColumns.LAST_NAME, label: getLocalizedText('lastName') },
    { value: FileUploadMatchColumns.LINKEDIN_URL, label: getLocalizedText('linkedinUrl') },
    { value: FileUploadMatchColumns.COMPANY, label: getLocalizedText('company') },
    { value: FileUploadMatchColumns.EMAIL, label: getLocalizedText('email') },
    { value: FileUploadMatchColumns.POSITION, label: getLocalizedText('position') },
    { value: FileUploadMatchColumns.CUSTOM_FIELD, label: getLocalizedText('customField') },
    { value: FileUploadMatchColumns.PHONE_NUMBER, label: getLocalizedText('phoneNumber') }, // <-- Added
  ];

  // Check if we have at least first or last name selected:
  const nameSelected = columns.some(
    (col) =>
      col.type === FileUploadMatchColumns.FIRST_NAME || col.type === FileUploadMatchColumns.LAST_NAME || col.type === FileUploadMatchColumns.FULL_NAME
  );

  // Check if we have a LinkedIn URL or a Company name:
  const linkedinUrlSelected = columns.some((col) => col.type === FileUploadMatchColumns.LINKEDIN_URL);
  const companySelected = columns.some((col) => col.type === FileUploadMatchColumns.COMPANY);

  // Check if Position/Occupation is selected
  const positionSelected = columns.some((col) => col.type === FileUploadMatchColumns.POSITION);

  // Decide if "Continue" button is disabled:
  // Allow either:
  // 1. Name + (LinkedIn URL OR Company) OR
  // 2. Position + Company
  const disabled = (!nameSelected || (!linkedinUrlSelected && !companySelected)) && (!positionSelected || !companySelected);

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <AttachFileIcon className={classes.titleIcon} />
            <Typography className={classes.titleText}>{fileName}</Typography>
          </Stack>
          <IconButton onClick={handleClose}>
            <CloseRoundedIcon className={classes.closeIcon} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <div>
            <Typography className={classes.blueTitle} mb={'8px'}>
              {getLocalizedText('labelTheFieldsOfYourContactList')}
            </Typography>
            <Typography className={classes.subTitle} mb={'8px'}>
              {getLocalizedText('thisIsHowItGoes')}
            </Typography>
            <Typography className={classes.greyText}>{getLocalizedText('mapFileValuesToContactProperties')}</Typography>
          </div>

          <div style={{ margin: '16px 0' }}>
            <PaiCheckbox checked={hasTableHeading} onChange={handleHasTableHeadingChange} label={getLocalizedText('fileHasTableHeading')} />
          </div>

          <Divider />

          <Stack direction={'row'} justifyContent={'space-between'} margin={'16px 0'}>
            <Typography className={classes.tableHead} sx={{ flex: 1 }}>
              {getLocalizedText('columnContentPreview')}
            </Typography>
            <Typography className={classes.tableHead} sx={{ flex: 1 }}>
              {getLocalizedText('columnLabel')}
            </Typography>
          </Stack>

          {columns.map((col, index) => (
            <Stack key={index} gap={1}>
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography className={classes.greyText} sx={{ flex: 1 }}>
                  {_.truncate(col.text, { length: 100 })}
                </Typography>
                <div style={{ flex: 1 }}>
                  <PaiSelect
                    value={col.type}
                    color={col.type === FileUploadMatchColumns.EMPTY ? theme.palette.greys.light : undefined}
                    onChange={handleColumnTypeChange(index)}
                    disabled={!col.text}
                  >
                    {manualCampaignContactUploadOptions.map((option) => (
                      <PaiMenuItem
                        key={option.value}
                        value={option.value}
                        hidden={
                          // Hide if the same column type was already selected
                          // but we allow "EMPTY" multiple times if user wants to skip multiple columns
                          columns.some((col) => col.type === option.value && option.value !== FileUploadMatchColumns.EMPTY)
                        }
                      >
                        {option.label}
                      </PaiMenuItem>
                    ))}
                  </PaiSelect>
                </div>
              </Stack>
              {index !== columns.length - 1 ? <Divider /> : null}
            </Stack>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <PaiButton text={getLocalizedText('continue')} action={handleAcceptColumns} border={'rounded'} disabled={disabled} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '12px',
    maxWidth: '1150px',
    width: '100%',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '8px 24px 8px 40px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    color: '#FFF',
  },
  titleIcon: {
    fontSize: theme.typography.pxToRem(32),
    color: '#FFF',
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: '#FFF',
  },
  content: {
    padding: '16px 40px !important',
    whiteSpace: 'pre-wrap',
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  tableHead: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '12px !important',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default FileUploadDialog;
